// Updated pokemonTcgApi.ts functions to work with boolean reaction columns
// This replaces the complex vote_type/reaction_type logic with simple boolean columns

import React from 'react';
import { Card } from 'pokemon-tcg-sdk-typescript';
import { PokemonCard, PokemonSet } from '../types';
import { supabase } from '../lib/supabase';
import { valuableCards } from '../data/valuableCards';
import { log } from '../components/DebugLogger';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { enrichWithScrydexPrices } from '../utils/cardPricing';

// Memory cache with improved structure
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  stale: boolean;
}

const memoryCache = new Map<string, CacheEntry<any>>();
const STALE_WHILE_REVALIDATE_DURATION = 2 * 60 * 1000; // 2 minutes
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// Background revalidation queue
const revalidationQueue = new Set<string>();
let isProcessingQueue = false;
let lastFocusTime = Date.now();

// Add tab focus listener
if (typeof window !== 'undefined') {
  window.addEventListener('focus', () => {
    const now = Date.now();
    // If more than 1 minute has passed since last focus, invalidate cache
    if (now - lastFocusTime > 60 * 1000) {
      log('Tab focused after inactivity, clearing cache');
      memoryCache.clear();
    }
    lastFocusTime = now;
  });
}

// Cache management functions
function getCacheKey(type: string, id: string): string {
  return `${type}:${id}`;
}

function getFromCache<T>(type: string, id: string): T | null {
  const key = getCacheKey(type, id);
  const entry = memoryCache.get(key);
  const now = Date.now();
  
  if (!entry) {
    log(`Cache miss for ${key}`);
    return null;
  }
  
  const age = now - entry.timestamp;
  
  // If cache is fresh, return immediately
  if (age < STALE_WHILE_REVALIDATE_DURATION) {
    log(`Cache hit (fresh) for ${key}`);
    return entry.data;
  }
  
  // If cache is stale, queue revalidation
  if (age < CACHE_DURATION && !entry.stale) {
    if (!entry.stale) {
      entry.stale = true;
      log(`Cache hit (stale) for ${key}, queueing revalidation`);
      queueRevalidation(type, id);
      return entry.data;
    }
  }
  
  // Cache is expired
  log(`Cache expired for ${key}, removing entry`);
  memoryCache.delete(key);
  return null;
}

function setInCache<T>(type: string, id: string, data: T): void {
  const key = getCacheKey(type, id);
  memoryCache.set(key, {
    data,
    timestamp: Date.now(),
    stale: false
  });
}

function queueRevalidation(type: string, id: string): void {
  revalidationQueue.add(getCacheKey(type, id));
  if (!isProcessingQueue) {
    processRevalidationQueue();
  }
}

async function processRevalidationQueue(): Promise<void> {
  if (isProcessingQueue || revalidationQueue.size === 0) return;
  
  isProcessingQueue = true;
  
  try {
    const promises: Promise<void>[] = [];
    
    for (const key of revalidationQueue) {
      const [type, id] = key.split(':');
      
      if (type === 'set') {
        promises.push(
          getSetCards(id, true)
            .then(() => revalidationQueue.delete(key))
            .catch(console.error)
        );
      }
      
      // Add delay between requests
      if (promises.length >= 3) {
        await Promise.all(promises);
        promises.length = 0;
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    await Promise.all(promises);
  } finally {
    isProcessingQueue = false;
    
    // If new items were added during processing, run again
    if (revalidationQueue.size > 0) {
      setTimeout(processRevalidationQueue, 100);
    }
  }
}

// Set name normalization mapping
const SET_NAME_MAPPING: Record<string, string> = {
  // Base Set Series
  'base': 'base1',
  'base set': 'base1',
  'jungle': 'base2',
  'fossil': 'base3',
  'base set 2': 'base4',
  'team rocket': 'base5',
  'gym heroes': 'gym1',
  'gym challenge': 'gym2',

  // Neo Series
  'neo genesis': 'neo1',
  'neo discovery': 'neo2',
  'neo revelation': 'neo3',
  'neo destiny': 'neo4',

  // Legendary Collection
  'legendary collection': 'base6',

  // e-Card Series
  'expedition base set': 'ecard1',
  'aquapolis': 'ecard2',
  'skyridge': 'ecard3',

  // EX Series
  'ex ruby sapphire': 'ex1',
  'ex ruby & sapphire': 'ex1',
  'ex ruby and sapphire': 'ex1',
  'ruby & sapphire': 'ex1',
  'ruby and sapphire': 'ex1',
  'ruby sapphire': 'ex1',
  'ruby-sapphire': 'ex1',
  'ex sandstorm': 'ex2',
  'sandstorm': 'ex2',
  'dragon': 'ex3',
  'ex dragon': 'ex3',
  'dragon ex': 'ex3',
  'ex series dragon': 'ex3',
  'ex team magma vs team aqua': 'ex4',
  'team magma vs team aqua': 'ex4',
  'ex hidden legends': 'ex5',
  'hidden legends': 'ex5',
  'ex firered leafgreen': 'ex6',
  'ex firered & leafgreen': 'ex6',
  'ex firered and leafgreen': 'ex6',
  'firered & leafgreen': 'ex6',
  'firered and leafgreen': 'ex6',
  'firered leafgreen': 'ex6',
  'firered-leafgreen': 'ex6',
  'ex team rocket returns': 'ex7',
  'team rocket returns': 'ex7',
  'ex deoxys': 'ex8',
  'deoxys': 'ex8',
  'ex emerald': 'ex9',
  'emerald': 'ex9',
  'ex unseen forces': 'ex10',
  'unseen forces': 'ex10',
  'ex delta species': 'ex11',
  'delta species': 'ex11',
  'ex legend maker': 'ex12',
  'legend maker': 'ex12',
  'ex holon phantoms': 'ex13',
  'holon phantoms': 'ex13',
  'ex crystal guardians': 'ex14',
  'crystal guardians': 'ex14',
  'ex dragon frontiers': 'ex15',
  'dragon frontiers': 'ex15',
  'ex power keepers': 'ex16',
  'power keepers': 'ex16',

  // POP Series
  'pop series 1': 'pop1',
  'pop series 2': 'pop2',
  'pop series 3': 'pop3',
  'pop series 4': 'pop4',
  'pop series 5': 'pop5',
  'pop series 6': 'pop6',
  'pop series 7': 'pop7',
  'pop series 8': 'pop8',
  'pop series 9': 'pop9',
  'pop 1': 'pop1',
  'pop 2': 'pop2',
  'pop 3': 'pop3',
  'pop 4': 'pop4',
  'pop 5': 'pop5',
  'pop 6': 'pop6',
  'pop 7': 'pop7',
  'pop 8': 'pop8',
  'pop 9': 'pop9',
  'pop': null,

  // Diamond & Pearl Series
  'diamond pearl': 'dp1',
  'diamond & pearl': 'dp1',
  'diamond and pearl': 'dp1',
  'mysterious treasures': 'dp2',
  'secret wonders': 'dp3',
  'great encounters': 'dp4',
  'majestic dawn': 'dp5',
  'legends awakened': 'dp6',
  'stormfront': 'dp7',

  // Platinum Series
  'platinum': 'pl1',
  'rising rivals': 'pl2',
  'supreme victors': 'pl3',
  'arceus': 'pl4',

  // HeartGold & SoulSilver Series
  'heartgold soulsilver': 'hgss1',
  'heartgold & soulsilver': 'hgss1',
  'heartgold and soulsilver': 'hgss1',
  'hs triumphant': 'hgss4',
  'hs—triumphant': 'hgss4',
  'hstriumphant': 'hgss4',
  'triumphant': 'hgss4',
  'hs undaunted': 'hgss3',
  'hs—undaunted': 'hgss3',
  'hsundaunted': 'hgss3',
  'undaunted': 'hgss3',
  'hs unleashed': 'hgss2',
  'hs—unleashed': 'hgss2',
  'hsunleashed': 'hgss2',
  'unleashed': 'hgss2',
  'call of legends': 'col1',

  // Black & White Series
  'black white': 'bw1',
  'black & white': 'bw1',
  'black and white': 'bw1',
  'emerging powers': 'bw2',
  'noble victories': 'bw3',
  'next destinies': 'bw4',
  'dark explorers': 'bw5',
  'dragons exalted': 'bw6',
  'dragon vault': 'dv1',
  'boundaries crossed': 'bw7',
  'plasma storm': 'bw8',
  'plasma freeze': 'bw9',
  'plasma blast': 'bw10',
  'legendary treasures': 'bw11',

  // XY Series
  'kalos starter set': 'xy0',
  'xy': 'xy1',
  'flashfire': 'xy2',
  'furious fists': 'xy3',
  'phantom forces': 'xy4',
  'primal clash': 'xy5',
  'double crisis': 'dc1',
  'roaring skies': 'xy6',
  'ancient origins': 'xy7',
  'breakthrough': 'xy8',
  'breakpoint': 'xy9',
  'generations': 'g1',
  'fates collide': 'xy10',
  'steam siege': 'xy11',
  'evolutions': 'xy12',

  // Sun & Moon Series
  'sun moon': 'sm1',
  'sun & moon': 'sm1',
  'sun and moon': 'sm1',
  'guardians rising': 'sm2',
  'burning shadows': 'sm3',
  'crimson invasion': 'sm4',
  'ultra prism': 'sm5',
  'forbidden light': 'sm6',
  'celestial storm': 'sm7',
  'dragon majesty': 'sm75',
  'lost thunder': 'sm8',
  'team up': 'sm9',
  'unbroken bonds': 'sm10',
  'unified minds': 'sm11',
  'cosmic eclipse': 'sm12',
  'hidden fates': 'sm115',
  'hidden fates shiny vault': 'sma',
  'shiny vault': 'sma',
  'shining legends': 'sm35',

  // Sword & Shield Series
  'sword shield': 'swsh1',
  'sword & shield': 'swsh1',
  'sword and shield': 'swsh1',
  'rebel clash': 'swsh2',
  'darkness ablaze': 'swsh3',
  'vivid voltage': 'swsh4',
  'battle styles': 'swsh5',
  'chilling reign': 'swsh6',
  'evolving skies': 'swsh7',
  'fusion strike': 'swsh8',
  'brilliant stars': 'swsh9',
  'brilliant stars trainer gallery': 'swsh9tg',
  'astral radiance': 'swsh10',
  'astral radiance trainer gallery': 'swsh10tg',
  'lost origin': 'swsh11',
  'lost origin trainer gallery': 'swsh11tg',
  'silver tempest': 'swsh12',
  'silver tempest trainer gallery': 'swsh12tg',
  'crown zenith': 'swsh12pt5',
  'crown zenith galarian gallery': 'swsh12pt5gg',
  'champions path': 'swsh35',
  'shining fates': 'swsh45',
  'shining fates shiny vault': 'swsh45sv',

  // Scarlet & Violet Series
  'surging sparks': 'sv8',
  'prismatic evolutions': 'sv8pt5',
  'scarlet & violet': 'sv1',
  'scarlet and violet': 'sv1',
  'scarlet violet': 'sv1',
  'temporal forces': 'sv5',
  'paldean fates': 'sv4pt5',
  'twilight masquerade': 'sv6',
  'shrouded fable': 'sv6pt5',
  'stellar crown': 'sv7',
  'journey together': 'sv9',
  'destined rivals': 'sv10',
  'black bolt': 'zsv10pt5',
  'white flare': 'rsv10pt5',
  'scarlet & violet black star promos': 'svp',
  'scarlet and violet black star promos': 'svp',
  'scarlet violet black star promos': 'svp',
  'scarlet & violet energies': 'sve',
  'scarlet and violet energies': 'sve',
  'scarlet violet energies': 'sve',
  'paldea evolved': 'sv2',
  'obsidian flames': 'sv3',
  '151': 'sv3pt5',
  'paradox rift': 'sv4',

  // Promos
  'wizards black star promos': 'basep',
  'nintendo black star promos': 'np',
  'nintendo promos': 'np',
  'black star promos': 'np',
  'dp black star promos': 'dpp',
  'hgss black star promos': 'hsp',
  'bw black star promos': 'bwp',
  'xy black star promos': 'xyp',
  'sm black star promos': 'smp',
  'swsh black star promos': 'swshp',

  // Other Sets
  'pokemon futsal collection': 'fut20',
  'pokemon futsal': 'fut20',
  'pokemon go': 'pgo',
  'pokemon-go': 'pgo',
  'pokémon go': 'pgo',
  'pokémon-go': 'pgo',
  'pokmon go': 'pgo',
  'pokmon-go': 'pgo',
  'sword shield pokemon go': 'pgo',
  'sword-shield pokemon-go': 'pgo',
  'sword & shield pokemon go': 'pgo',
  'sword and shield pokemon go': 'pgo',
  'pokemon rumble': 'ru1',
  'pokmon rumble': 'ru1',
  'rumble': 'ru1',
  'detective pikachu': 'det1',
  'detective pikachu series': 'det1',
  'celebrations classic collection': 'cel25c',
  'celebrations: classic collection': 'cel25c',
  'classic collection': 'cel25c',
  'celebrations': 'cel25',
  'southern islands': 'si1',
  'best of game': 'bp',

  // McDonald's Collections
  'mcdonalds collection 2011': 'mcd11',
  'mcdonalds collection 2012': 'mcd12',
  'mcdonalds collection 2013': 'mcd13',
  'mcdonalds collection 2014': 'mcd14',
  'mcdonalds collection 2015': 'mcd15',
  'mcdonalds collection 2016': 'mcd16',
  'mcdonalds collection 2017': 'mcd17',
  'mcdonalds collection 2018': 'mcd18',
  'mcdonalds collection 2019': 'mcd19',
  'mcdonalds collection 2021': 'mcd21',
  'mcdonalds collection 2022': 'mcd22',
  'mcdonalds collection 2023': 'mcd23',
  'mcdonalds collection': null,
  'mcdonalds promo': null,
  'mcdonalds promos': null,

  // Trainer Kits
  'ex trainer kit latias': 'tk1a',
  'ex trainer kit latios': 'tk1b',
  'ex trainer kit 2 plusle': 'tk2a',
  'ex trainer kit 2 minun': 'tk2b',

  // Mega Evolution Series
  'mega evolution': 'me1',
  'phantasmal flames': 'me2',
  'ascended heroes': 'me2pt5',
  'mega evolution black star promos': 'mep'
};

// Function to normalize set names to their IDs
function normalizeSetName(setName: string): string | null {
  const normalized = setName
    .toLowerCase()
    .trim()
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ');

  // Debug logs
  console.log('Normalizing set name:', setName, 'to:', normalized);
  console.log('SET_NAME_MAPPING keys:', Object.keys(SET_NAME_MAPPING));

  const exactMatch = SET_NAME_MAPPING[normalized];
  if (exactMatch) {
    console.log('Found exact match:', exactMatch);
    return exactMatch;
  }

  // Try with '&' replaced with 'and'
  const andMatch = SET_NAME_MAPPING[normalized.replace(/ & /g, ' and ')];
  if (andMatch) {
    console.log('Found match with "and":', andMatch);
    return andMatch;
  }

  // Fallback for common typos and variants
  if (normalized === 'pokmon go') {
    return SET_NAME_MAPPING['pokemon go'];
  }
  if (normalized === 'pokmon futsal collection') {
    return SET_NAME_MAPPING['pokemon futsal collection'];
  }
  if (normalized === 'pokmon rumble') {
    return SET_NAME_MAPPING['pokemon rumble'];
  }
  if (normalized === 'firered leafgreen') {
    return 'ex6';
  }
  if (normalized === 'ruby sapphire') {
    return 'ex1';
  }

  console.log('No mapping found for:', normalized);
  return null;
}

async function getAllSets(): Promise<PokemonSet[]> {
  try {
    // Check memory cache first
    const cachedSets = getFromCache<PokemonSet[]>('sets', 'all');
    if (cachedSets) {
      return cachedSets;
    }

    const { data, error } = await supabase
      .from('pokemontcg_io_sets')
      .select('*')
      .order('release_date', { ascending: false });
    
    if (error) {
      console.error('Error fetching sets:', error);
      return [];
    }
    
    // Transform data to match PokemonSet interface
    const sortedSets = data.map(set => ({
      ...set,
      releaseDate: set.release_date,
      images: {
        symbol: set.symbol_url || null,
        logo: set.logo_url || null
      }
    })).sort((a: PokemonSet, b: PokemonSet) =>
      new Date(b.release_date).getTime() - new Date(a.release_date).getTime()
    );
    
    // Cache the results
    setInCache('sets', 'all', sortedSets);
    
    return sortedSets;
  } catch (error) {
    console.error('Error fetching sets:', error);
    throw new Error('Failed to fetch sets. Please try again later.');
  }
}

async function searchAllCards(query: string): Promise<Card[]> {
  try {
    if (!query?.trim()) return [];

    // Check if searching for McDonald's cards by year
    const mcdonaldsMatch = query.toLowerCase().match(/mcdonalds.*?(20\d{2}|collection)/);
    if (mcdonaldsMatch) {
      const year = mcdonaldsMatch[1];
      const yearSuffix = year.match(/20\d{2}/) ? year.slice(-2) : '';
      const setId = yearSuffix ? `mcd${yearSuffix}` : null;

      console.log('Searching for McDonald\'s cards');
      let dbQuery = supabase
        .from('pokemontcg_io')
        .select(`
          *,
          set:pokemontcg_io_sets(*)
        `);

      // If year specified, filter by specific set ID
      if (setId) {
        console.log('Filtering by McDonald\'s set:', setId);
        dbQuery = dbQuery.eq('set_id', setId);
      } else {
        // Otherwise show all McDonald's cards
        console.log('Showing all McDonald\'s cards');
        dbQuery = dbQuery.ilike('id', 'mcd%');
      }

      const { data, error } = await dbQuery
        .order('release_date', { ascending: false });

      if (error) {
        console.error('Error searching McDonald\'s cards:', error);
        return [];
      }

      if (!data) {
        console.log(`No McDonald's cards found${setId ? ` for ${year}` : ''}`);
        return [];
      }

      console.log(`Found ${data.length} McDonald's cards${setId ? ` for ${year}` : ''}`);
      const mapped = data.map(card => ({
        ...card,
        flavor: card.flavor_text,
        images: {
          small: card.image_small_url,
          large: card.image_large_url
        },
        set: {
          ...card.set,
          releaseDate: card.set.release_date,
          images: {
            symbol: card.set.symbol_url,
            logo: card.set.logo_url
          }
        }
      }));
      return enrichWithScrydexPrices(mapped);
    }

    // Extract search terms
    const terms = query.match(/([^:]+):("[^"]+"|[^\s]+)/g) || [];
    let searchQuery = query;

    // Build the database query
    let dbQuery = supabase.from('pokemontcg_io').select(`
      id,
      name,
      number,
      set_id,
      set_name,
      series,
      set_total,
      release_date,
      market_price_usd,
      market_price_low_usd,
      market_price_trend_usd,
      market_price_reverse_holo_usd,
      hp,
      types,
      evolves_from,
      evolves_to,
      abilities,
      attacks,
      weaknesses,
      retreat_cost,
      rarity,
      image_small_url,
      image_large_url,
      flavor_text
    `);

    // Handle special search terms
    terms.forEach(term => {
      const [field, value] = term.split(':');
      const cleanValue = value.replace(/"/g, '').trim();

      switch (field.trim()) {
        case 'name':
          dbQuery = dbQuery.ilike('name', `%${cleanValue}%`);
          break;
        case 'set.name':
          dbQuery = dbQuery.ilike('set_name', `%${cleanValue}%`);
          break;
        case 'number':
          dbQuery = dbQuery.eq('number', cleanValue);
          break;
      }
      // Remove the processed term from the general search
      searchQuery = searchQuery.replace(term, '').trim();
    });

    // Add general search if no specific terms
    if (!terms.length && searchQuery) {
      dbQuery = dbQuery.ilike('name', `%${searchQuery}%`);
    }
    
    // Execute query with limit
    const { data, error } = await dbQuery.limit(1000);
    
    if (error) {
      console.error('Error searching cards:', error);
      return [];
    }

    if (!data) {
      console.log('No cards found for query:', query);
      return [];
    }
    
    // Validate card data
    const validCards = data.filter((card: any) => (
      card?.id &&
      card?.name &&
      card?.set_id &&
      card?.set_name &&
      card?.number
    ));

    console.log(`Found ${validCards.length} valid cards`);
    const mapped = validCards.map(card => ({
      ...card,
      flavor: card.flavor_text,
      images: {
        small: card.image_small_url,
        large: card.image_large_url
      },
      set: {
        id: card.set_id,
        name: card.set_name,
        series: card.series,
        total: card.set_total,
        releaseDate: card.release_date
      }
    }));
    return enrichWithScrydexPrices(mapped);
  } catch (error) {
    console.error('Error searching cards:', error);
    throw new Error('Failed to search cards. Please try again later.');
  }
}

async function getSetCards(setId: string, isRevalidation = false): Promise<Card[]> {
  try {
    if (!setId) {
      console.error('Invalid setId provided to getSetCards');
      return [];
    }

    // Check memory cache first
    if (!isRevalidation) {
      const cachedCards = getFromCache<Card[]>('set', setId);
      if (cachedCards) {
        console.log(`Returning ${cachedCards.length} cached cards for set ${setId}`);
        return cachedCards;
      }
    }

    // First try to get all cards at once (set limit high to ensure we get all)
    const { data: allCards, error } = await supabase
      .from('pokemontcg_io')
      .select(`
        *,
        set:pokemontcg_io_sets(*)
      `)
      .eq('set_id', setId)
      .order('number')
      .limit(1000); // Most sets have < 300 cards, but set high to be safe
    
    if (error) {
      console.error('Error fetching cards:', error);
      throw error;
    }

    if (!allCards || allCards.length === 0) {
      console.log('No cards found for set:', setId);
      return [];
    }


    // Transform the cards
    const transformedCards = allCards.map(card => ({
      ...card,
      flavor: card.flavor_text,
      images: {
        small: card.image_small_url,
        large: card.image_large_url
      },
      set: {
        ...card.set,
        releaseDate: card.set.release_date,
        images: {
          symbol: card.set.symbol_url,
          logo: card.set.logo_url
        }
      }
    }));

    // Enrich with Scrydex NM prices
    const enrichedCards = await enrichWithScrydexPrices(transformedCards);
    
    // Cache the results
    if (!isRevalidation) {
      setInCache('set', setId, enrichedCards);
    }
    
    return enrichedCards;
  } catch (error) {
    console.error('Error fetching set cards:', error instanceof Error ? error.message : error);
    return []; // Return empty array instead of throwing to prevent infinite loading
  }
}

async function getCard(cardId: string): Promise<Card | null> {
  try {
    if (!cardId) {
      throw new Error('No cardId provided');
    }

    // First get the main card data
    const { data, error } = await supabase
      .from('pokemontcg_io')
      .select(`
        *,
        set:pokemontcg_io_sets(*),
        evolves_from_id,
        evolves_to_id
      `)
      .eq('id', cardId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching card:', error);
      return null;
    }

    if (data) {
      // Get evolution chain IDs if not already present
      if (data.evolves_from) {
        const { data: preEvo } = await supabase
          .from('pokemontcg_io')
          .select('id, name')
          .eq('name', data.evolves_from)
          .eq('set_id', data.set_id)
          .maybeSingle();
        
        if (preEvo) {
          data.evolves_from_id = preEvo.id;
          data.evolves_from = preEvo.name;
        }
      }

      if (data.evolves_to && data.evolves_to.length > 0) {
        const { data: evo } = await supabase
          .from('pokemontcg_io')
          .select('id, name')
          .eq('name', data.evolves_to[0])
          .eq('set_id', data.set_id)
          .maybeSingle();
        
        if (evo) {
          data.evolves_to_id = [evo.id];
          data.evolves_to = [evo.name];
        }
      }
    }
    if (!data) return null;

    const card = {
      ...data,
      flavor: data.flavor_text,
      images: {
        small: data.image_small_url,
        large: data.image_large_url
      },
      set: {
        ...data.set,
        releaseDate: data.set.release_date,
        images: {
          symbol: data.set.symbol_url,
          logo: data.set.logo_url
        }
      }
    };

    const [enriched] = await enrichWithScrydexPrices([card]);
    return enriched;
  } catch (error) {
    console.error('Error fetching card:', error);
    throw new Error('Failed to fetch card details. Please try again later.');
  }
}

async function getSimilarCards(card: Card): Promise<Card[]> {
  try {
    if (!card?.name || !card?.id) {
      console.log('Invalid card data for similar cards:', card);
      return [];
    }
    
    // Get base name without any suffixes (V, VMAX, etc)
    const baseName = card.name.split(' ')[0];
    
    const { data, error } = await supabase
      .from('pokemontcg_io')
      .select(`
        *,
        set:pokemontcg_io_sets(*)
      `)
      .ilike('name', `${baseName}%`)
      .neq('id', card.id)
      .order('release_date', { ascending: false })
      .limit(50);
    
    if (error) {
      console.error('Error fetching similar cards:', error);
      return [];
    }

    if (!data) {
      console.log('No similar cards found');
      return [];
    }
    
    
    
    const similarCards = data.map(card => ({
      ...card,
      flavor: card.flavor_text,
      images: {
        small: card.image_small_url,
        large: card.image_large_url
      },
      set: {
        ...card.set,
        releaseDate: card.set.release_date,
        images: {
          symbol: card.set.symbol_url,
          logo: card.set.logo_url
        }
      }
    }));

    console.log(`Found ${similarCards.length} similar cards`);
    return enrichWithScrydexPrices(similarCards);
  } catch (error) {
    console.error('Error fetching similar cards:', error);
    return [];
  }
}

async function getMostValuableCards(limit: number = 10): Promise<Card[]> {
  try {
    // Fetch cards sorted by market_price_trend_usd
    const { data, error } = await supabase
      .from('pokemontcg_io')
      .select(`
        *,
        set:pokemontcg_io_sets(*)
      `)
      .not('market_price_trend_usd', 'is', null)
      .order('market_price_trend_usd', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('Error fetching valuable cards:', error);
      return valuableCards.slice(0, limit);
    }
    
    if (!data) {
      console.log('No valuable cards found, using static data');
      return valuableCards.slice(0, limit);
    }
    
    // Validate and filter cards
    const validCards = data.filter((card: any) => (
      card?.id &&
      card?.name &&
      card?.set?.id &&
      card?.set?.id &&
      card?.number &&
      card.market_price_trend_usd > 0
    ));

    console.log(`Found ${validCards.length} valid valuable cards`);
    
    // Transform data to match Card interface
    const result = validCards.map(card => ({
      ...card,
      flavor: card.flavor_text,
      images: {
        small: card.image_small_url,
        large: card.image_large_url
      },
      set: {
        ...card.set,
        releaseDate: card.set.release_date,
        images: {
          symbol: card.set.symbol_url,
          logo: card.set.logo_url
        }
      },
      cardmarket: {
        prices: {
          averageSellPrice: card.market_price_trend_usd,
          lowPrice: card.market_price_low_usd,
          trendPrice: card.market_price_trend_usd,
          reverseHoloTrend: card.market_price_reverse_holo_usd || 0
        }
      }
    })).slice(0, limit);
    return enrichWithScrydexPrices(result);
  } catch (error) {
    console.error('Error fetching valuable cards:', error instanceof Error ? error.message : error);
    // Fallback to static data if API fails
    console.log('Falling back to static valuable cards data');
    return valuableCards.slice(0, limit);
  }
}

async function getFoilCardsFromCollection(foilItems: any[]): Promise<Card[]> {
  try {
    const cardPromises = foilItems.map(item => 
      fetch(`https://api.pokemontcg.io/v2/cards/${item.cardId}`, {
        headers: {
          'X-Api-Key': '51f8f8de-7ac2-4d08-8172-9d3e2c7ac48d'
        }
      }).then(res => res.json())
    );
    
    const cardResponses = await Promise.all(cardPromises);
    const validCards = cardResponses
      .filter(response => response.data)
      .map(response => response.data);
    
    // Map collection quantities to cards
    const cardsWithQuantities = validCards.flatMap(card =>
      Array.from({ length: foilItems.find(item => item.cardId === card.id)?.quantity || 0 })
        .map((_, index) => ({
          ...card,
          uniqueId: `${card.id}-${Date.now()}-${index}`,
          images: {
            small: card.image_small_url,
            large: card.image_large_url
          }
        }))
    );

    return cardsWithQuantities;
  } catch (error) {
    console.error('Error fetching cards:', error);
    return [];
  }
}

// Community Sentiment API Functions

// Simplified reaction types - just like/dislike
export const REACTION_TYPES = {
  like: { icon: ThumbsUp, label: 'Like', color: 'text-green-500' },
  dislike: { icon: ThumbsDown, label: 'Dislike', color: 'text-red-500' }
} as const;

export type ReactionType = keyof typeof REACTION_TYPES;

// Interface for card with sentiment data
interface CardWithSentiment extends Card {
  vote_stats: {
    // Simple like/dislike counts
    likes_count: number;
    dislikes_count: number;
    total_votes: number;
    
    // User's reaction (if any)
    user_reaction: ReactionType | null;
    
    // Sentiment score (0-1, where 1 is 100% likes)
    sentiment_score: number;
    current_position: number;
    position_change?: number;
  };
  // Ensure these exist from Card interface
  id: string;
  name: string;
  number: string;
  market_price_usd?: number;
  // Add missing properties used in CommunityPage
  set: {
    name: string;
    series: string;
    total?: number;
    releaseDate?: string;
  };
  images: {
    small: string;
    large?: string;
  };
}

// Simplified sorting and filtering options
export type SortOption = 'most_votes' | 'most_liked' | 'most_recent';
export type FilterOption = 'all' | 'liked' | 'disliked';

// Optimized version with caching and performance improvements
async function getMostLikedCards(
  limit: number = 50, 
  sortBy: SortOption = 'most_votes',
  filterBy: FilterOption = 'all'
): Promise<CardWithSentiment[]> {
  // Use cache key that includes parameters
  const cacheKey = `most_liked_cards_${limit}_${sortBy}_${filterBy}`;
  // Temporarily disable cache to ensure fresh data after schema change
  // const cached = getFromCache<CardWithSentiment[]>('community', cacheKey);
  // if (cached) {
  //   return cached;
  // }

  // Use the optimized fallback method
  const results = await getMostLikedCardsFallback(limit, sortBy, filterBy);
  
  // Temporarily disable caching after schema change
  // setInCache('community', cacheKey, results);
  
  return results;
}

// Simplified method to work with like/dislike only
async function getMostLikedCardsFallback(
  limit: number = 50, 
  sortBy: SortOption = 'most_votes',
  filterBy: FilterOption = 'all'
): Promise<CardWithSentiment[]> {
  try {
    console.log('Getting most liked cards with simplified structure:', { limit, sortBy, filterBy });

    // Get vote statistics using the simplified boolean columns
    let query = supabase
      .from('card_votes')
      .select(`
        card_id,
        liked,
        disliked,
        created_at
      `)
; // Get all votes, not just last 30 days

    const { data: rawData, error: reactionError } = await query
      .order('created_at', { ascending: false })
      .limit(10000); // Limit raw data to prevent memory issues

    if (reactionError) {
      console.error('Error fetching reaction data:', reactionError);
      return [];
    }

    if (!rawData || rawData.length === 0) {
      console.log('No reaction data found');
      return [];
    }

    console.log('Raw reaction data:', rawData.length, 'rows');

    // Aggregate votes by card_id
    const voteCounts = rawData.reduce((acc: Record<string, { likes: number; dislikes: number; latestVote: string }>, row: any) => {
      if (!acc[row.card_id]) {
        acc[row.card_id] = {
          likes: 0,
          dislikes: 0,
          latestVote: row.created_at
        };
      }
      
      // Count true boolean values
      if (row.liked) acc[row.card_id].likes++;
      if (row.disliked) acc[row.card_id].dislikes++;
      
      // Track latest vote for sorting
      if (row.created_at > acc[row.card_id].latestVote) {
        acc[row.card_id].latestVote = row.created_at;
      }
      
      return acc;
    }, {} as Record<string, { likes: number; dislikes: number; latestVote: string }>);

    console.log('Aggregated vote counts:', Object.keys(voteCounts).length, 'cards');
    console.log('Sample vote counts:', Object.entries(voteCounts).slice(0, 3));

    if (Object.keys(voteCounts).length === 0) {
      return [];
    }

    // Apply filter if specified
    let filteredCounts = voteCounts;
    if (filterBy === 'liked') {
      filteredCounts = Object.fromEntries(
        Object.entries(voteCounts).filter(([_, counts]) => (counts as any).likes > 0)
      );
    } else if (filterBy === 'disliked') {
      filteredCounts = Object.fromEntries(
        Object.entries(voteCounts).filter(([_, counts]) => (counts as any).dislikes > 0)
      );
    }

    if (Object.keys(filteredCounts).length === 0) {
      return [];
    }

    // Calculate sentiment scores and sort
    const cardSentiments = Object.entries(filteredCounts)
      .map(([cardId, counts]) => {
        const countsTyped = counts as { likes: number; dislikes: number; latestVote: string };
        const likesCount = countsTyped.likes;
        const dislikesCount = countsTyped.dislikes;
        const totalVotes = likesCount + dislikesCount;
        
        // Calculate sentiment score (0-1, where 1 is 100% likes)
        const sentimentScore = totalVotes > 0 ? (likesCount / totalVotes) : 0;
        
        // Calculate score based on sort option
        let score = 0;
        switch (sortBy) {
          case 'most_votes':
            score = totalVotes;
            break;
          case 'most_liked':
            score = likesCount;
            break;
          case 'most_recent':
            score = new Date(countsTyped.latestVote).getTime();
            break;
          default:
            score = totalVotes;
        }

        return {
          cardId,
          likesCount,
          dislikesCount,
          totalVotes,
          sentimentScore,
          latestVote: countsTyped.latestVote,
          sortScore: score
        };
      })
      .filter(item => item.totalVotes > 0)
      .sort((a, b) => b.sortScore - a.sortScore)
      .slice(0, limit);

    if (cardSentiments.length === 0) {
      return [];
    }

    // Get card IDs for database lookup
    const topCardIds = cardSentiments.map(item => item.cardId);

    // Fetch card details from pokemontcg_io table
    const { data: cardData, error: cardError } = await supabase
      .from('pokemontcg_io')
      .select(`
        *,
        set:pokemontcg_io_sets(*)
      `)
      .in('id', topCardIds);

    if (cardError) {
      console.error('Error fetching card data:', cardError);
      return [];
    }

    if (!cardData || cardData.length === 0) {
      return [];
    }

    // Transform data to match Card interface and add vote stats
    const cards: CardWithSentiment[] = cardSentiments
      .map((sentiment, index) => {
        const card = cardData.find((c: any) => c.id === sentiment.cardId);
        if (!card) return null;

        return {
          ...card,
          images: {
            small: card.image_small_url,
            large: card.image_large_url
          },
          set: {
            ...card.set,
            releaseDate: card.set?.release_date || '',
            images: {
              symbol: card.set?.symbol_url || '',
              logo: card.set?.logo_url || ''
            }
          },
          vote_stats: {
            likes_count: sentiment.likesCount,
            dislikes_count: sentiment.dislikesCount,
            total_votes: sentiment.totalVotes,
            user_reaction: null, // Will be populated later if needed
            sentiment_score: sentiment.sentimentScore,
            current_position: index + 1,
            position_change: undefined
          }
        } as CardWithSentiment;
      })
      .filter(Boolean) as CardWithSentiment[];

    return cards;
  } catch (error) {
    console.error('Error in getMostLikedCardsFallback:', error);
    return [];
  }
}

// Get cards voted on by a specific user - SIMPLIFIED FOR LIKE/DISLIKE ONLY
async function getUserLikedCards(userId: string, reactionFilter?: ReactionType): Promise<CardWithSentiment[]> {
  try {
    console.log('Getting user voted cards with simplified structure:', { userId, reactionFilter });

    // Build query based on filter
    let query = supabase
      .from('card_votes')
      .select(`
        card_id,
        liked,
        disliked,
        created_at,
        updated_at
      `)
      .eq('user_id', userId);

    // Apply filter
    if (reactionFilter === 'like') {
      query = query.eq('liked', true);
    } else if (reactionFilter === 'dislike') {
      query = query.eq('disliked', true);
    }

    const { data: rawUserReactions, error: userReactionsError } = await query
      .order('updated_at', { ascending: false });

    if (userReactionsError) {
      console.error('Error fetching user reactions:', userReactionsError);
      return [];
    }

    if (!rawUserReactions || rawUserReactions.length === 0) {
      console.log('No user reactions found');
      return [];
    }

    // Process user reactions
    const userReactionsMap = new Map<string, {
      reaction: ReactionType,
      created_at: string
    }>();

    rawUserReactions.forEach((row: any) => {
      let reaction: ReactionType | null = null;
      
      // Determine the user's reaction
      if (row.liked) reaction = 'like';
      else if (row.disliked) reaction = 'dislike';
      
      // Only include cards that have a reaction
      if (reaction) {
        userReactionsMap.set(row.card_id, {
          reaction,
          created_at: row.created_at
        });
      }
    });

    if (userReactionsMap.size === 0) {
      console.log('No filtered user reactions found');
      return [];
    }

    // Get unique card IDs and sort by most recent
    const cardIds = Array.from(userReactionsMap.keys());
    const cardsSortedByTime = cardIds.sort((a, b) => {
      const timeA = userReactionsMap.get(a)?.created_at || '';
      const timeB = userReactionsMap.get(b)?.created_at || '';
      return timeB.localeCompare(timeA); // Most recent first
    });

    console.log('Processed user reactions:', cardsSortedByTime.length, 'cards');

    // Fetch card details
    const { data: cardData, error: cardError } = await supabase
      .from('pokemontcg_io')
      .select(`
        *,
        set:pokemontcg_io_sets(*)
      `)
      .in('id', cardIds);

    if (cardError) {
      console.error('Error fetching card data:', cardError);
      return [];
    }

    if (!cardData || cardData.length === 0) {
      return [];
    }

    // Get all votes for each card to show community stats
    const { data: rawAllVotes, error: allVotesError } = await supabase
      .from('card_votes')
      .select('card_id, liked, disliked')
      .in('card_id', cardIds);

    if (allVotesError) {
      console.error('Error fetching all votes:', allVotesError);
      return [];
    }

    // Count votes per card
    const voteCounts = (rawAllVotes || []).reduce((acc: Record<string, { likes: number; dislikes: number }>, row: any) => {
      if (!acc[row.card_id]) {
        acc[row.card_id] = {
          likes: 0,
          dislikes: 0
        };
      }
      
      // Count true boolean values
      if (row.liked) acc[row.card_id].likes++;
      if (row.disliked) acc[row.card_id].dislikes++;
      
      return acc;
    }, {} as Record<string, { likes: number; dislikes: number }>);

    // Transform data and maintain chronological order
    const cards: CardWithSentiment[] = cardsSortedByTime
      .map((cardId, index) => {
        const card = cardData.find(c => c.id === cardId);
        if (!card) return null;

        const counts = voteCounts[cardId] || { likes: 0, dislikes: 0 };
        
        // Calculate sentiment metrics
        const likesCount = counts.likes;
        const dislikesCount = counts.dislikes;
        const totalVotes = likesCount + dislikesCount;
        
        // Simple sentiment score (0-1, where 1 is 100% likes)
        const sentimentScore = totalVotes > 0 ? (likesCount / totalVotes) : 0;

        return {
          ...card,
          images: {
            small: card.image_small_url,
            large: card.image_large_url
          },
          set: {
            ...card.set,
            releaseDate: card.set?.release_date || '',
            images: {
              symbol: card.set?.symbol_url || '',
              logo: card.set?.logo_url || ''
            }
          },
          vote_stats: {
            likes_count: likesCount,
            dislikes_count: dislikesCount,
            total_votes: totalVotes,
            user_reaction: userReactionsMap.get(cardId)?.reaction || null,
            sentiment_score: sentimentScore,
            current_position: index + 1,
            position_change: undefined
          }
        } as CardWithSentiment;
      })
      .filter(Boolean) as CardWithSentiment[];

    return cards;
  } catch (error) {
    console.error('Error in getUserLikedCards:', error);
    return [];
  }
}

// Add or toggle a vote on a card - SIMPLIFIED FOR LIKE/DISLIKE ONLY
async function addCardReaction(userId: string, cardId: string, reactionType: ReactionType): Promise<boolean> {
  try {
    console.log('Adding/toggling vote:', { userId, cardId, reactionType });
    
    // Check if user already has a vote for this card
    const { data: existingVote } = await supabase
      .from('card_votes')
      .select('liked, disliked')
      .eq('user_id', userId)
      .eq('card_id', cardId)
      .single();
    
    // Determine the new vote state
    let newLiked = false;
    let newDisliked = false;
    
    if (reactionType === 'like') {
      // If already liked, remove the like (toggle off)
      // If disliked or no vote, set to liked
      newLiked = !existingVote?.liked;
      newDisliked = false;
    } else if (reactionType === 'dislike') {
      // If already disliked, remove the dislike (toggle off)
      // If liked or no vote, set to disliked
      newLiked = false;
      newDisliked = !existingVote?.disliked;
    }
    
    // If both are false after toggle, delete the record
    if (!newLiked && !newDisliked) {
      const { error } = await supabase
        .from('card_votes')
        .delete()
        .eq('user_id', userId)
        .eq('card_id', cardId);
      
      if (error) {
        console.error('Error removing vote:', error);
        return false;
      }
    } else {
      // Upsert the vote
      const updateData = {
        user_id: userId,
        card_id: cardId,
        liked: newLiked,
        disliked: newDisliked,
        updated_at: new Date().toISOString()
      };
      
      const { error } = await supabase
        .from('card_votes')
        .upsert(updateData, { 
          onConflict: 'user_id,card_id'
        });
      
      if (error) {
        console.error('Error upserting vote:', error);
        return false;
      }
    }
    
    console.log('Successfully updated vote:', { reactionType, newLiked, newDisliked });
    return true;
  } catch (error) {
    console.error('Error in addCardReaction:', error);
    return false;
  }
}

// Remove a vote from a card - SIMPLIFIED FOR LIKE/DISLIKE ONLY
async function removeCardReaction(userId: string, cardId: string, reactionType?: ReactionType): Promise<boolean> {
  try {
    console.log('Removing vote:', { userId, cardId, reactionType });
    
    // For simplified system, just delete the entire vote record
    const { error } = await supabase
      .from('card_votes')
      .delete()
      .eq('user_id', userId)
      .eq('card_id', cardId);
    
    if (error) {
      console.error('Error removing vote:', error);
      return false;
    }
    
    console.log('Successfully removed vote');
    return true;
  } catch (error) {
    console.error('Error in removeCardReaction:', error);
    return false;
  }
}

// Get user's current vote for a card - SIMPLIFIED FOR LIKE/DISLIKE ONLY
async function getUserCardReactions(userId: string, cardId: string): Promise<ReactionType[]> {
  try {
    console.log('Getting user vote:', { userId, cardId });
    
    const { data, error } = await supabase
      .from('card_votes')
      .select('liked, disliked')
      .eq('user_id', userId)
      .eq('card_id', cardId)
      .single();

    if (error) {
      // No vote found is not an error in this context
      if (error.code === 'PGRST116') {
        console.log('No vote found for user');
        return [];
      }
      console.error('Error fetching user vote:', error);
      return [];
    }

    if (!data) {
      console.log('No vote found for user');
      return [];
    }

    const reactions: ReactionType[] = [];

    // Convert boolean values to reaction array
    if (data.liked) reactions.push('like');
    if (data.disliked) reactions.push('dislike');

    console.log('User vote:', reactions);
    return reactions;
  } catch (error) {
    console.error('Error in getUserCardReactions:', error);
    return [];
  }
}

// Get user's current reaction to a card (legacy - returns first reaction found)
async function getUserCardReaction(userId: string, cardId: string): Promise<ReactionType | null> {
  try {
    const reactions = await getUserCardReactions(userId, cardId);
    return reactions.length > 0 ? reactions[0] : null;
  } catch (error) {
    console.error('Error in getUserCardReaction:', error);
    return null;
  }
}

// Legacy function for backward compatibility
async function removeCardLike(userId: string, cardId: string): Promise<boolean> {
  return removeCardReaction(userId, cardId, 'love_artwork');
}

// Note: Primary votes (like/dislike) are now handled through the regular reaction functions
// Use addCardReaction(userId, cardId, 'like') or addCardReaction(userId, cardId, 'dislike')

// Single export statement for all functions
export {
  getAllSets,
  searchAllCards,
  getSetCards,
  getCard,
  getSimilarCards,
  getMostValuableCards,
  normalizeSetName,
  getFoilCardsFromCollection,
  getMostLikedCards,
  getUserLikedCards,
  addCardReaction,
  removeCardReaction,
  getUserCardReaction,
  getUserCardReactions,
  removeCardLike // Legacy function
};

// Export types and constants
export type { CardWithSentiment };